import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Volume2, VolumeX, X, Sparkles, Maximize2, Minimize2, PhoneCall, PhoneOff } from 'lucide-react';
import { Select } from '@dreamtree-org/twreact-ui';
import ToolTraceStack from '@/components/ToolTraceStack';
import MessageActions from '@/components/MessageActions';
import ChatActionsMenu from '@/components/ChatActionsMenu';
import MessageBubble from '@/components/MessageBubble';
import AskUserCard from '@/components/AskUserCard';
import Composer from '@/components/Composer';
import AgentCharacter from '@/components/AgentCharacter';
import VoiceDock from '@/components/VoiceDock';
import { useCharacterSpeech } from '@/lib/useCharacterSpeech';
import { useVoiceMode } from '@/lib/useVoiceMode';
import { useAppApi } from '@/lib/api';

/** Human label for a message's author — assistant turns are the selected agent. */
function speakerFor(role, agentName) {
  if (role === 'user') return 'You';
  if (role === 'assistant') return agentName;
  return role;
}

// The picker defaults to the most recently created agents; typing searches across
// ALL of them. Keeps the header usable as the agent roster grows to hundreds.
const DEFAULT_AGENT_COUNT = 5;

/**
 * Searchable agent picker. Closed/unsearched, it shows only the latest
 * DEFAULT_AGENT_COUNT agents (by id desc) plus the currently-selected one (so the
 * value always resolves). Typing filters the full roster by name or key. When there
 * are ≤ DEFAULT_AGENT_COUNT agents this is identical to showing them all.
 */
function AgentPicker({ agents, agentKey, onChangeAgent }) {
  const [searching, setSearching] = useState(false);
  const options = useMemo(() => {
    // While the user is typing, offer the whole roster and let Select filter it by
    // label; otherwise only the latest few. Either way keep the selected agent present
    // so its label always resolves in the closed control.
    const base = searching
      ? agents
      : [...agents].sort((a, b) => (b.id || 0) - (a.id || 0)).slice(0, DEFAULT_AGENT_COUNT);
    const selected = agents.find((a) => a.key === agentKey);
    const list = selected && !base.some((a) => a.key === selected.key) ? [selected, ...base] : base;
    return list.map((a) => ({ label: a.name, value: a.key }));
  }, [agents, agentKey, searching]);

  // The wrapper (not the Select's own className) is the flex item that shrinks — a
  // long agent name must never widen the header and push the mute/menu/close controls
  // off the edge. min-w-0 lets it shrink below content; overflow-hidden clips any
  // non-truncating overflow (the dropdown itself is portaled, so it isn't clipped).
  return (
    <div className="flex-1 min-w-0 overflow-hidden">
      <Select
        className="w-full"
        options={options}
        value={agentKey}
        onChange={onChangeAgent}
        onSearch={(term) => setSearching(!!term && term.trim().length > 0)}
        searchable
        placeholder="Search agents…"
      />
    </div>
  );
}

function ChatHeader({
  agents, agentKey, onChangeAgent, showPicker, onClose, expanded, onToggleExpanded,
  characterState, mouthOpen, speechSupported, muted, onToggleMuted,
  voiceModeHook, serverSttReady,
}) {
  return (
    <div className="px-3 pt-3 flex items-center gap-2">
      <AgentCharacter state={characterState} mouthOpen={mouthOpen} />
      {showPicker && agents.length > 0 && (
        <AgentPicker agents={agents} agentKey={agentKey} onChangeAgent={onChangeAgent} />
      )}
      {serverSttReady && (
        <button
          type="button"
          onClick={() => voiceModeHook.toggleVoiceMode()}
          title={voiceModeHook.voiceMode ? 'End voice mode' : 'Start voice mode (speak to chat)'}
          aria-label={voiceModeHook.voiceMode ? 'End voice mode' : 'Start voice mode'}
          aria-pressed={voiceModeHook.voiceMode}
          className={`p-1.5 rounded-lg shrink-0 transition-colors ${voiceModeHook.voiceMode ? 'text-white bg-red-500 hover:bg-red-600' : 'text-secondary-500 hover:bg-[var(--app-border)]/40'}`}
        >
          {voiceModeHook.voiceMode ? <PhoneOff size={16} /> : <PhoneCall size={16} />}
        </button>
      )}
      {speechSupported && (
        <button
          type="button"
          onClick={onToggleMuted}
          title={muted ? 'Unmute agent voice' : 'Mute agent voice'}
          aria-label={muted ? 'Unmute agent voice' : 'Mute agent voice'}
          className="p-1.5 rounded-lg text-secondary-500 hover:bg-[var(--app-border)]/40 shrink-0 transition-colors"
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
      )}
      {onToggleExpanded && (
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-pressed={expanded}
          title={expanded ? 'Restore chat size' : 'Expand chat'}
          aria-label={expanded ? 'Restore chat size' : 'Expand chat'}
          className="p-1.5 rounded-lg text-secondary-500 hover:bg-[var(--app-border)]/40 shrink-0 transition-colors"
        >
          {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
      )}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          title="Close chat"
          aria-label="Close chat"
          className="p-1.5 rounded-lg text-secondary-500 hover:bg-[var(--app-border)]/40 shrink-0 transition-colors"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}

/** The area below the message list while a turn is in flight — either the ask_user
 *  question card, or a plain "thinking…" row with a live tool-trace badge. */
function PendingArea({ sending, pendingJob, onAnswerQuestion }) {
  if (!sending) return null;
  if (pendingJob?.status === 'waiting_for_user' && pendingJob.question) {
    return <AskUserCard question={pendingJob.question} onAnswer={onAnswerQuestion} />;
  }
  return (
    <div className="flex items-center gap-2 text-sm text-secondary-500">
      <Sparkles size={13} className="text-primary-500 animate-pulse" />
      thinking…
      {pendingJob?.traces?.length > 0 && <ToolTraceStack liveTraces={pendingJob.traces} liveBelt={pendingJob.belt} />}
    </div>
  );
}

/**
 * Presentational chat surface shared by the floating dock (AgentChatDock.jsx) and the
 * full-page Chat.jsx — both are thin wrappers around useAgentChat() + this component,
 * so the actual thread/input/agent-picker markup exists in exactly one place. The
 * attach/paste/clear-chat/file-preview building blocks (Composer, MessageFile,
 * ClearChatButton) are themselves shared further with the public embed widget
 * (EmbedChat.jsx) — see those components' own comments.
 *
 * The "Auto" toggle sets approvalMode for the next turn (HITL-TOOLING.md §1.3) — the
 * Human Proxy decides non-destructive gated tool calls inline either way
 * (see services/approvals.service.js); destructive calls are always denied.
 */
export default function ChatPanel({
  agents, agentKey, onChangeAgent, sessionId, messages, sending, pendingJob, onSend, onAnswerQuestion, showPicker = true,
  approvalMode, onToggleApprovalMode, onUploadAttachment, onClearChat, onClose,
  expanded, onToggleExpanded,
}) {
  const bottomRef = useRef(null);
  const { fetchApi } = useAppApi();
  // Every callback below is memoized on purpose. Passing fresh closures made
  // useCharacterSpeech's `speak` and useVoiceMode's `startCapture` change identity on
  // every render, which starved the voice loop's re-arm timer so the mic was never
  // requested at all — see the loop effect's comment in lib/useVoiceMode.js.
  // twreact-ui's useApi rebuilds fetchApi every render, so depending on it directly
  // would defeat the memoization — reach it through a ref instead.
  const fetchApiRef = useRef(fetchApi);
  fetchApiRef.current = fetchApi;
  const fetchVoiceStatus = useCallback(() => fetchApiRef.current('voiceSettingsStatus'), []);
  const synthesize = useCallback((text) => fetchApiRef.current('ttsSynthesize', { text }), []);
  const { supported: speechSupported, speaking, mouthOpen, muted, toggleMuted, speak, cancel, serverSttReady } = useCharacterSpeech({
    fetchStatus: fetchVoiceStatus,
    synthesize,
  });
  // The voice `send` needs the hook's own consumePendingPatch, which doesn't exist until
  // the hook returns — read it through a ref rather than recreating the callback.
  const voiceHookRef = useRef(null);
  const agentId = agents.find((a) => a.key === agentKey)?.id;
  const voiceSend = useCallback(
    (text) => onSend(text, undefined, voiceHookRef.current?.consumePendingPatch()),
    [onSend],
  );
  const voiceCorrect = useCallback(async (rawText) => {
    const res = await fetchApiRef.current('sttCorrect', { text: rawText, agentId });
    return res && res.text ? res.text : rawText;
  }, [agentId]);
  const voiceTranscribe = useCallback(async (blob) => {
    const formData = new FormData();
    formData.append('audio', blob, 'voice.webm');
    const res = await fetchApiRef.current('sttTranscribe', formData);
    return res && res.text;
  }, []);
  // Prompt optimization (composer Sparkles button) — same fail-soft stance as
  // voiceCorrect: server returns the raw draft on any model failure.
  const optimizePrompt = useCallback(async (draftText) => {
    const res = await fetchApiRef.current('promptOptimize', { text: draftText, agentId });
    return res && res.text ? res.text : draftText;
  }, [agentId]);
  const voiceModeHook = useVoiceMode({
    speak,
    cancel,
    send: voiceSend,
    answerQuestion: onAnswerQuestion,
    correct: voiceCorrect,
    pendingJob,
    approvalMode,
    setApprovalMode: onToggleApprovalMode,
    transcribe: voiceTranscribe,
    serverSttReady,
    sending,
    speaking,
  });
  voiceHookRef.current = voiceModeHook;
  const prevSendingRef = useRef(sending);
  const prevSessionRef = useRef(sessionId);

  // This user's 👍/👎 per message (messageId → {rating, comment}), loaded with the
  // session so past votes render as filled thumbs.
  const [feedback, setFeedback] = useState({});
  useEffect(() => {
    if (!sessionId) { setFeedback({}); return; }
    fetchApi('sessionFeedback', sessionId)
      .then((res) => {
        const map = {};
        (res.feedback || []).forEach((f) => { map[f.message_id] = { rating: f.rating, comment: f.comment }; });
        setFeedback(map);
      })
      .catch(() => {});
  }, [sessionId]);

  // Optimistic; a failed POST/DELETE just leaves the optimistic state (feedback is
  // low-stakes, not worth a rollback dance).
  const voteFeedback = async (messageId, rating) => {
    if (!rating) {
      setFeedback((m) => { const next = { ...m }; delete next[messageId]; return next; });
      try { await fetchApi('messageFeedbackClear', messageId); } catch { /* ignore */ }
      return;
    }
    setFeedback((m) => ({ ...m, [messageId]: { rating, comment: m[messageId]?.comment ?? null } }));
    try { await fetchApi('messageFeedback', { messageId, rating }); } catch { /* ignore */ }
  };
  const commentFeedback = async (messageId, comment) => {
    setFeedback((m) => ({ ...m, [messageId]: { rating: m[messageId]?.rating || 'down', comment } }));
    try { await fetchApi('messageFeedback', { messageId, rating: feedback[messageId]?.rating || 'down', comment }); } catch { /* ignore */ }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Speak the reply exactly when a turn completes (sending flips true -> false) —
  // never on history load, since a resumed/loaded session's `sending` starts (and
  // stays) false the whole time, so this edge just never fires for it. Diffing
  // messages by id instead was tried and dropped: `sessionId` can commit a render
  // before that session's `messages` finish loading (separate async state updates —
  // see useAgentChat.js), so seeding "already spoken" ids against still-stale
  // messages caused the entire history to be read aloud once it arrived.
  useEffect(() => {
    const sessionChanged = prevSessionRef.current !== sessionId;
    const wasSending = prevSendingRef.current;
    prevSendingRef.current = sending;
    prevSessionRef.current = sessionId;
    if (sessionChanged || !wasSending || sending) return; // guard against a mid-turn agent/session switch being misread as "just finished"
    const last = messages[messages.length - 1];
    if (last?.role === 'assistant') speak(last.content);
  }, [sending, messages, sessionId, speak]);

  // Barge-in: don't let the character keep talking over a turn the user just started.
  useEffect(() => {
    if (sending) cancel();
  }, [sending, cancel]);

  let characterState = 'idle';
  if (sending) characterState = 'thinking';
  else if (speaking) characterState = 'talking';

  const agentName = agents.find((a) => a.key === agentKey)?.name || 'Assistant';

  // Copy the whole thread as plain text (labelled by speaker), for pasting into a
  // ticket/notes. Assistant turns are attributed to the currently-selected agent.
  const copyConversation = () => {
    const text = messages.map((m) => `${speakerFor(m.role, agentName)}: ${m.content || ''}`).join('\n\n');
    navigator.clipboard.writeText(text);
  };

  // Copy the whole thread as structured JSON WITH each turn's tool-execution trace
  // (fetched per tool-using message from the same endpoint the trace badge uses) —
  // for debugging/handoff where the tool inputs/outputs matter, not just the text.
  const copyConversationJson = async () => {
    const withTraces = await Promise.all(messages.map(async (m) => {
      const entry = {
        role: m.role,
        speaker: speakerFor(m.role, agentName),
        content: m.content || '',
        messageId: m.id ?? null,
        createdAt: m.created_at ?? null,
      };
      if (m.toolCallCount && m.id) {
        try {
          const res = await fetchApi('agentSessionTraces', { sessionId, messageId: m.id });
          entry.toolTraces = res?.traces || [];
        } catch {
          entry.toolTraces = [];
        }
      }
      return entry;
    }));
    const payload = {
      agent: agentName, agentKey, sessionId, exportedAt: new Date().toISOString(), messages: withTraces,
    };
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
  };

  return (
    <div className="flex flex-col h-full">
      <ChatHeader
        agents={agents} agentKey={agentKey} onChangeAgent={onChangeAgent} showPicker={showPicker}
        onClose={onClose} expanded={expanded} onToggleExpanded={onToggleExpanded}
        characterState={characterState} mouthOpen={mouthOpen}
        speechSupported={speechSupported} muted={muted} onToggleMuted={toggleMuted}
        voiceModeHook={voiceModeHook}
        serverSttReady={serverSttReady}
      />
      {voiceModeHook.voiceMode && <VoiceDock state={voiceModeHook.dockState} onHangUp={voiceModeHook.toggleVoiceMode} />}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
        {messages.map((m, i) => (
          <MessageBubble
            key={m.id || i}
            message={m}
            downloadUrl={m.file ? `/api/agent/files/${m.file.id}` : null}
            actions={m.role === 'assistant' ? (
              <MessageActions
                message={m}
                feedback={feedback[m.id]}
                trace={m.toolCallCount ? (
              <ToolTraceStack
                sessionId={sessionId} messageId={m.id} count={m.toolCallCount}
                fetchTraces={(args) => fetchApi('agentSessionTraces', args)}
              />
            ) : null}
                onVote={(rating) => voteFeedback(m.id, rating)}
                onComment={(comment) => commentFeedback(m.id, comment)}
              />
            ) : null}
          />
        ))}
        <PendingArea sending={sending} pendingJob={pendingJob} onAnswerQuestion={onAnswerQuestion} />
        <div ref={bottomRef} />
      </div>
      <Composer
        sending={sending} disabled={!agentKey} onSend={onSend} onUploadAttachment={onUploadAttachment}
        downloadUrlFor={(fileId) => `/api/agent/files/${fileId}`}
        optimizePrompt={optimizePrompt}
        serverDictation={serverSttReady ? {
          supported: true,
          transcribe: async (blob) => {
            const formData = new FormData();
            formData.append('audio', blob, 'dictation.webm');
            const res = await fetchApi('sttTranscribe', formData);
            return res && res.text;
          },
        } : undefined}
        menu={(
          <ChatActionsMenu
            approvalMode={approvalMode} onToggleApprovalMode={onToggleApprovalMode}
            onCopyConversation={copyConversation} onCopyConversationJson={copyConversationJson}
            onClearChat={onClearChat} hasMessages={messages.length > 0}
          />
        )}
      />
    </div>
  );
}
